/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const DigitalMarketingServiceDetails = () => {
  const { t } = useTranslation();

  const services = [
    {
      title: t("digitalMarketing.services.socialMedia.title"),
      description: t("digitalMarketing.services.socialMedia.description"),
      features: t("digitalMarketing.services.socialMedia.features", {
        returnObjects: true,
      }),
      icon: "📱",
      color: "from-slate-700 to-slate-800",
    },
    {
      title: t("digitalMarketing.services.adwords.title"),
      description: t("digitalMarketing.services.adwords.description"),
      features: t("digitalMarketing.services.adwords.features", {
        returnObjects: true,
      }),
      icon: "🔍",
      color: "from-slate-600 to-slate-700",
    },
    {
      title: t("digitalMarketing.services.customized.title"),
      description: t("digitalMarketing.services.customized.description"),
      features: t("digitalMarketing.services.customized.features", {
        returnObjects: true,
      }),
      icon: "🎯",
      color: "from-slate-800 to-slate-900",
    },
  ];

  const marketingTools = [
    {
      title: t("digitalMarketing.tools.analytics.title"),
      description: t("digitalMarketing.tools.analytics.description"),
      icon: "📊",
    },
    {
      title: t("digitalMarketing.tools.content.title"),
      description: t("digitalMarketing.tools.content.description"),
      icon: "✍️",
    },
    {
      title: t("digitalMarketing.tools.seo.title"),
      description: t("digitalMarketing.tools.seo.description"),
      icon: "🔝",
    },
    {
      title: t("digitalMarketing.tools.social.title"),
      description: t("digitalMarketing.tools.social.description"),
      icon: "🔄",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Page Title */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          {t("digitalMarketing.title")}
        </h1>
        <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-1/2"
          >
            <div className="bg-digital-marketing rounded-lg p-8 shadow-xl">
              <div className="text-6xl mb-4">🚀</div>
              <h2 className="text-2xl font-bold text-white mb-4">
                {t("digitalMarketing.hero.title")}
              </h2>
              <p className="text-gray-200">
                {t("digitalMarketing.hero.description")}
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full md:w-1/2"
          >
            <div className="grid grid-cols-2 gap-4">
              {marketingTools.map((tool, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="text-3xl mb-2">{tool.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    {tool.title}
                  </h3>
                  <p className="text-sm text-gray-600">{tool.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {t("digitalMarketing.services.title")}
          </h2>
          <div className="w-20 h-1 bg-slate-600 mx-auto mb-4"></div>
          <p className="text-gray-700 max-w-2xl mx-auto">
            {t("digitalMarketing.services.description")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`bg-gradient-to-br ${service.color} rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {service.title}
              </h3>
              <p className="text-gray-200 mb-4">{service.description}</p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-200">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg p-8 text-center"
        >
          <h2 className="text-3xl font-bold text-white mb-4">
            {t("digitalMarketing.cta.title")}
          </h2>
          <p className="text-gray-200 mb-6 max-w-2xl mx-auto">
            {t("digitalMarketing.cta.description")}
          </p>
          <motion.a
            href="/contact-us"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-slate-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
          >
            {t("digitalMarketing.cta.button")}
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};

export default DigitalMarketingServiceDetails;
